import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { User } from '@supabase/supabase-js';

interface ExerciseLogData {
  exercise_type: string;
  exercise_name: string;
  category: string;
  duration_minutes?: number;
  completion_data?: Record<string, any>;
  symptoms_before?: Record<string, number>;
  symptoms_after?: Record<string, number>;
  target_symptoms?: string[];
  notes?: string;
  xp_earned?: number;
}

interface SymptomCorrelation {
  symptom_key: string;
  symptom_label: string;
  exercise_type: string;
  average_improvement: number;
  times_tracked: number;
  trend: 'improving' | 'stable' | 'declining';
}

interface ExerciseInsight {
  exercise_name: string;
  times_completed: number;
  total_xp: number;
  avg_symptom_improvements: Record<string, number>;
  best_for_symptoms: string[];
}

export const useUniversalTracking = () => {
  const [user, setUser] = useState<User | null>(null);
  const [isLogging, setIsLogging] = useState(false);

  useEffect(() => {
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
    };
    getUser();
  }, []);

  const logExercise = async (data: ExerciseLogData): Promise<boolean> => {
    if (!user) {
      toast.error('Please sign in to track exercises');
      return false;
    }

    setIsLogging(true);
    try {
      const { error } = await supabase
        .from('therapeutic_exercise_logs')
        .insert({
          user_id: user.id,
          exercise_type: data.exercise_type,
          exercise_name: data.exercise_name,
          category: data.category,
          duration_minutes: data.duration_minutes,
          completion_data: data.completion_data || {},
          symptoms_before: data.symptoms_before || {},
          symptoms_after: data.symptoms_after || {},
          target_symptoms: data.target_symptoms || [],
          notes: data.notes,
          xp_earned: data.xp_earned || 10
        });

      if (error) throw error;

      // Calculate improvements for toast
      const improvements: string[] = [];
      if (data.symptoms_before && data.symptoms_after) {
        for (const [key, beforeVal] of Object.entries(data.symptoms_before)) {
          const afterVal = data.symptoms_after[key];
          if (afterVal !== undefined) {
            const diff = afterVal - beforeVal;
            // For negative symptoms (anxiety, etc.), improvement is negative diff
            // For positive symptoms (mood, energy), improvement is positive diff
            const isNegativeSymptom = ['anxiety_level', 'stress_level', 'hopelessness', 'urge_to_harm', 
              'intrusive_thoughts', 'compulsion_urges', 'craving_intensity', 'flashback_intensity',
              'hypervigilance', 'panic_symptoms', 'avoidance_urges', 'distress_level', 'self_criticism',
              'emotional_numbing', 'body_image', 'eating_urges', 'food_anxiety', 'trigger_reactivity',
              'sleep_anxiety', 'emotional_pain', 'worry_intensity', 'physical_tension'].includes(key);
            
            if ((isNegativeSymptom && diff < 0) || (!isNegativeSymptom && diff > 0)) {
              improvements.push(`${formatSymptomKey(key)} ${isNegativeSymptom ? '↓' : '↑'}`);
            }
          }
        }
      }

      const improvementText = improvements.length > 0 
        ? `Improved: ${improvements.slice(0, 3).join(', ')}`
        : 'Progress tracked!';

      toast.success(`Exercise completed! +${data.xp_earned || 10} XP`, {
        description: improvementText
      });
      return true;
    } catch (error) {
      console.error('Error logging exercise:', error);
      toast.error('Could not log exercise');
      return false;
    } finally {
      setIsLogging(false);
    }
  };

  const getSymptomCorrelations = async (days = 30): Promise<SymptomCorrelation[]> => {
    if (!user) return [];

    try {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      const { data: logs, error } = await supabase
        .from('therapeutic_exercise_logs')
        .select('*')
        .eq('user_id', user.id)
        .gte('created_at', startDate.toISOString())
        .order('created_at', { ascending: false });

      if (error) throw error;
      if (!logs || logs.length === 0) return [];

      // Aggregate by symptom and exercise type
      const correlations: Map<string, {
        symptom_key: string;
        exercise_type: string;
        improvements: number[];
      }> = new Map();

      for (const log of logs) {
        const before = (log.symptoms_before as Record<string, number>) || {};
        const after = (log.symptoms_after as Record<string, number>) || {};

        for (const [symptomKey, beforeVal] of Object.entries(before)) {
          const afterVal = after[symptomKey];
          if (afterVal === undefined) continue;

          const key = `${symptomKey}_${log.exercise_type}`;
          const existing = correlations.get(key) || {
            symptom_key: symptomKey,
            exercise_type: log.exercise_type,
            improvements: []
          };

          // Calculate improvement (negative for negative symptoms is good)
          const isNegativeSymptom = isNegativeSymptomKey(symptomKey);
          const improvement = isNegativeSymptom 
            ? beforeVal - afterVal  // Lower is better
            : afterVal - beforeVal; // Higher is better

          existing.improvements.push(improvement);
          correlations.set(key, existing);
        }
      }

      // Convert to result format
      const results: SymptomCorrelation[] = [];
      for (const data of correlations.values()) {
        if (data.improvements.length < 2) continue; // Need at least 2 data points

        const avg = data.improvements.reduce((a, b) => a + b, 0) / data.improvements.length;
        const recentAvg = data.improvements.slice(0, Math.ceil(data.improvements.length / 2))
          .reduce((a, b) => a + b, 0) / Math.ceil(data.improvements.length / 2);
        const olderAvg = data.improvements.slice(Math.ceil(data.improvements.length / 2))
          .reduce((a, b) => a + b, 0) / Math.floor(data.improvements.length / 2);

        results.push({
          symptom_key: data.symptom_key,
          symptom_label: formatSymptomKey(data.symptom_key),
          exercise_type: data.exercise_type,
          average_improvement: Math.round(avg * 10) / 10,
          times_tracked: data.improvements.length,
          trend: recentAvg > olderAvg + 0.5 ? 'improving' : recentAvg < olderAvg - 0.5 ? 'declining' : 'stable'
        });
      }

      // Sort by average improvement
      return results.sort((a, b) => b.average_improvement - a.average_improvement);
    } catch (error) {
      console.error('Error getting correlations:', error);
      return [];
    }
  };

  const getExerciseInsights = async (days = 30): Promise<ExerciseInsight[]> => {
    if (!user) return [];

    try {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      const { data: logs, error } = await supabase
        .from('therapeutic_exercise_logs')
        .select('*')
        .eq('user_id', user.id)
        .gte('created_at', startDate.toISOString());

      if (error) throw error;
      if (!logs || logs.length === 0) return [];

      const exerciseMap: Map<string, ExerciseInsight> = new Map();

      for (const log of logs) {
        const existing = exerciseMap.get(log.exercise_name) || {
          exercise_name: log.exercise_name,
          times_completed: 0,
          total_xp: 0,
          avg_symptom_improvements: {},
          best_for_symptoms: []
        };

        existing.times_completed++;
        existing.total_xp += log.xp_earned || 10;

        // Calculate symptom improvements
        const before = (log.symptoms_before as Record<string, number>) || {};
        const after = (log.symptoms_after as Record<string, number>) || {};

        for (const [symptomKey, beforeVal] of Object.entries(before)) {
          const afterVal = after[symptomKey];
          if (afterVal === undefined) continue;

          const isNegative = isNegativeSymptomKey(symptomKey);
          const improvement = isNegative ? beforeVal - afterVal : afterVal - beforeVal;

          const currentAvg = existing.avg_symptom_improvements[symptomKey] || 0;
          const count = logs.filter(l => 
            l.exercise_name === log.exercise_name && 
            (l.symptoms_before as Record<string, number>)?.[symptomKey] !== undefined
          ).length;

          existing.avg_symptom_improvements[symptomKey] = 
            (currentAvg * (count - 1) + improvement) / count;
        }

        exerciseMap.set(log.exercise_name, existing);
      }

      // Find best symptoms for each exercise
      for (const insight of exerciseMap.values()) {
        const sortedSymptoms = Object.entries(insight.avg_symptom_improvements)
          .filter(([_, avg]) => avg > 0.5)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 3);
        
        insight.best_for_symptoms = sortedSymptoms.map(([key]) => formatSymptomKey(key));
      }

      return Array.from(exerciseMap.values())
        .sort((a, b) => b.times_completed - a.times_completed);
    } catch (error) {
      console.error('Error getting insights:', error);
      return [];
    }
  };

  const getPersonalizedRecommendation = async (): Promise<string | null> => {
    const insights = await getExerciseInsights(14);
    const correlations = await getSymptomCorrelations(14);

    if (insights.length === 0) return null;

    // Find what's working best
    const bestCorrelation = correlations[0];
    if (bestCorrelation && bestCorrelation.average_improvement > 1) {
      return `${bestCorrelation.exercise_type.replace(/_/g, ' ')} is helping your ${bestCorrelation.symptom_label.toLowerCase()} - average improvement of ${bestCorrelation.average_improvement.toFixed(1)} points per session!`;
    }

    // Find most consistent exercise
    const mostConsistent = insights.find(i => i.times_completed >= 3);
    if (mostConsistent) {
      return `You've done ${mostConsistent.exercise_name} ${mostConsistent.times_completed} times. Consistency builds lasting change!`;
    }

    return null;
  };

  return {
    logExercise,
    getSymptomCorrelations,
    getExerciseInsights,
    getPersonalizedRecommendation,
    isLogging,
    user
  };
};

function isNegativeSymptomKey(key: string): boolean {
  return ['anxiety_level', 'stress_level', 'hopelessness', 'urge_to_harm', 
    'intrusive_thoughts', 'compulsion_urges', 'craving_intensity', 'flashback_intensity',
    'hypervigilance', 'panic_symptoms', 'avoidance_urges', 'distress_level', 'self_criticism',
    'emotional_numbing', 'body_image', 'eating_urges', 'food_anxiety', 'trigger_reactivity',
    'sleep_anxiety', 'emotional_pain', 'worry_intensity', 'physical_tension', 'urge_to_use',
    'anhedonia'].includes(key);
}

function formatSymptomKey(key: string): string {
  return key
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
